import React, { useEffect, useState } from 'react';
import { Box, Typography, Divider, debounce } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { RemoveRedEyeOutlined } from '@mui/icons-material';
import moment from 'moment';
import { SmartFilter } from '../../components/common/SmartFilter';
import { ListTable } from '../../components/molecules/ListTable';
import Pagination from '../../components/atoms/Pagination';
import { QuoteRequestHeader } from '../../utils/constants';
import { GetQuoteRequests } from '../../services/quoteRequests';
import { capitalizeWords } from '../../utils/helper';

/**
 * Generic list page for user-app quote requests (AMC / Sell Old Boiler /
 * Expert Advice / Site Visit). Clones the Sell Old AC page so the table UI
 * stays consistent: SmartFilter -> ListTable -> Pagination, same columns.
 */
const QuoteRequestList: React.FC<{ title: string; types: string }> = ({
  title,
  types,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [requests, setRequests] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>();

  useEffect(() => {
    fetchRequests(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage, searchQuery, types]);

  const fetchRequests = async (
    currentPage: number,
    limit: number,
    search?: string,
  ) => {
    try {
      setIsLoading(true);
      const response = await GetQuoteRequests({
        types,
        page: currentPage,
        limit,
        searchQuery: search,
      });
      setRequests(response.data);
      setTotalCount(response.pagination.totalCount);
    } catch (error) {
      console.error('Failed to fetch quote requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshList = () => {
    fetchRequests(page, rowsPerPage, searchQuery);
  };

  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1); // API expects 1-based page index
  };

  const actions = [
    {
      icon: <RemoveRedEyeOutlined />,
      onClick: (item: any) =>
        navigate(`/request/quote-detail`, {
          // returnPath lets the sidebar keep highlighting this list (see
          // getActiveMenuPath) since /request/quote-detail is shared across
          // Sell Old Boiler / Expert Advice / Site Visit and has no menu
          // entry of its own.
          state: { id: item?._id, returnPath: location.pathname },
        }),
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <SmartFilter
        title={title}
        createFilter={['SEARCH']}
        filterdrawerSize="sm"
        filterHeader={`Filter ${title}`}
        onSearch={handleSearchRefresh}
        onSaved={refreshList}
      />
      <Divider sx={{ marginBottom: 3 }} />
      <Box
        sx={{
          height: '100%',
          maxHeight: '100%',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
          p:1
        }}
      >
        {requests.length <= 0 && !isLoading ? (
          <Typography textAlign={'center'} marginTop={'20%'}>
            No Requests Found
          </Typography>
        ) : (
          <ListTable
            headerCells={QuoteRequestHeader}
            rows={requests}
            isLoading={isLoading}
            actions={actions}
            renderCell={(request: any, colIndex: number) => {
              switch (colIndex) {
                case 0:
                  return capitalizeWords(request.enquiryId) || 'N/A';
                case 1:
                  return capitalizeWords(request.user?.name) || 'N/A';
                case 2:
                  return request.subType || 'N/A';
                case 3:
                  return request.technicianName || 'N/A';
                case 4:
                  return request.user?.phoneNumber || 'N/A';
                case 5:
                  // expert-advice style requests may have no schedule at all
                  return request.schedule?.date ? (
                    <div className="flex flex-col">
                      <span>
                        {request.schedule.slot == 'SECOND_HALF'
                          ? '2nd Half'
                          : '1st Half'}
                      </span>
                      <span>
                        {moment(request.schedule.date).format('DD/MM/yyyy')}
                      </span>
                    </div>
                  ) : (
                    moment(request.createdAt).format('DD/MM/yyyy')
                  );
                case 6:
                  return request.status
                    ? request.status.charAt(0).toUpperCase() +
                        request.status.slice(1).toLowerCase()
                    : 'N/A';
                default:
                  return null;
              }
            }}
          />
        )}
      </Box>
      <Box display={'block'} bottom={0} right={0}>
        <Pagination
          count={totalCount || 0}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

export default QuoteRequestList;
